import { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

type View = 'buttons' | 'email' | 'sent';

export default function AuthPage() {
  const { signInWithMagicLink } = useAuth();
  const [view, setView] = useState<View>('buttons');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      console.error('Google sign-in error:', error.message);
      window.alert('Unable to start Google sign-in. Please try again.');
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { error } = await signInWithMagicLink(email.trim());

    setSubmitting(false);

    if (error) {
      setError(error);
    } else {
      setView('sent');
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-white px-6">
      <div className="w-full max-w-md rounded-3xl border border-black/10 bg-white p-8 text-center shadow-sm">
        <img
          src="/images/runcollogo.svg"
          alt="Run Collective"
          className="mx-auto h-16 w-auto"
        />
        <p className="mt-3 text-sm text-gray-500">
          Discover local runs, follow clubs, and stay in the loop.
        </p>

        {view === 'buttons' && (
          <div className="mt-8 space-y-4">
            <button
              type="button"
              onClick={signInWithGoogle}
              className="flex w-full items-center justify-center gap-3 rounded-xl border border-black/10 bg-white px-4 py-3 font-medium shadow-sm transition hover:bg-gray-50"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-5 w-5 shrink-0">
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v8.51h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.14z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="#FBBC05" d="M10.53 28.58A14.9 14.9 0 0 1 9.6 24c0-1.58.27-3.12.93-4.58l-7.98-6.19A23.93 23.93 0 0 0 0 24c0 3.77.9 7.34 2.55 10.77l7.98-6.19z"/>
                <path fill="#EA4335" d="M24 9.52c3.53 0 6.69 1.22 9.19 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.55 13.23l7.98 6.19C12.43 13.74 17.74 9.52 24 9.52z"/>
              </svg>
              Sign in with Google
            </button>

            <button
              type="button"
              onClick={() => setView('email')}
              className="w-full rounded-xl bg-black px-4 py-3 font-medium text-white transition hover:opacity-90"
            >
              Continue with Email
            </button>
          </div>
        )}

        {view === 'email' && (
          <form onSubmit={handleEmailSubmit} className="mt-8 space-y-4 text-left">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-black">
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-black/10 px-4 py-3 text-sm outline-none transition focus:border-black"
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-black px-4 py-3 font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send magic link'}
            </button>

            <button
              type="button"
              onClick={() => { setView('buttons'); setError(null); }}
              className="w-full text-center text-sm text-black/45 transition hover:text-black"
            >
              Back
            </button>
          </form>
        )}

        {view === 'sent' && (
          <div className="mt-8 space-y-3">
            <p className="text-sm font-medium text-black">Check your inbox</p>
            <p className="text-sm text-black/55">
              We sent a sign-in link to <span className="font-medium text-black">{email}</span>.
              Click it to continue.
            </p>
            <button
              type="button"
              onClick={() => { setView('buttons'); setEmail(''); setError(null); }}
              className="mt-4 text-sm text-black/45 transition hover:text-black"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}